import type {
  OverwatchMapOddElementsPagedQuery,
  OverwatchMapOddElementsPagedQueryVariables,
} from './MapOverlays.hook'

/**
 * Max elements requested per page. The paged endpoint caps `limit` at 5000, so
 * using the max minimises round-trips while keeping each response under the
 * graphql-adapter Lambda's 6 MB limit (MAT-77839) — the whole reason the un-paged
 * `oddElements` call returned 0 elements for large metros like LA.
 */
export const ODD_ELEMENTS_PAGE_SIZE = 5000

export type OddElementsPageElement =
  OverwatchMapOddElementsPagedQuery['oddElementsPaged']['elements'][number]

type PagedFilterVariables = Omit<
  OverwatchMapOddElementsPagedQueryVariables,
  'offset' | 'limit'
>

type FetchOddElementsPage = (options: {
  variables: OverwatchMapOddElementsPagedQueryVariables
}) => Promise<{ data?: OverwatchMapOddElementsPagedQuery | null }>

/**
 * Fetch every ODD element for the given filters by walking the offset/limit
 * pages until `totalCount` is reached.
 *
 * The paged endpoint slices a fully materialised list, so `offset` advances by
 * the number of elements actually returned and the loop stops once we've
 * collected `totalCount` — or a page comes back empty, which guards against a
 * stuck loop if the backend ever reports a `totalCount` it can't deliver.
 */
export const fetchAllOddElements = async (
  fetchPage: FetchOddElementsPage,
  variables: PagedFilterVariables,
  pageSize: number = ODD_ELEMENTS_PAGE_SIZE,
): Promise<OddElementsPageElement[]> => {
  const collected: OddElementsPageElement[] = []
  let offset = 0
  let totalCount = 0

  do {
    const result = await fetchPage({
      variables: { ...variables, offset, limit: pageSize },
    })
    const page = result.data?.oddElementsPaged
    // Throw rather than return what we have: a page failing mid-walk (GraphQL/transport
    // error -> null data) would otherwise hand callers a truncated set as if complete,
    // turning the empty-map bug (MAT-77839) into a silent partial-map one. Callers'
    // error handling can then react instead of trusting bad data.
    if (!page) {
      throw new Error(
        `oddElementsPaged returned no data at offset ${offset} (${collected.length}/${totalCount} fetched)`,
      )
    }

    collected.push(...page.elements)
    totalCount = page.totalCount
    offset += page.elements.length

    // An empty page while still short of totalCount means the backend can't deliver what
    // it promised. Break the loop (no infinite spin) but throw for the same reason as above.
    if (page.elements.length === 0 && collected.length < totalCount) {
      throw new Error(
        `oddElementsPaged returned an empty page with ${collected.length}/${totalCount} fetched`,
      )
    }
  } while (collected.length < totalCount)

  return collected
}
